import {
    BootstrapSizes,
  Button,
  ButtonColors,
  Modal,
} from "@talentech/components";
import { Suspense, useEffect, useMemo, useState } from "react";
import { useEditor } from "@grapesjs/react";
import { Component, Editor } from "grapesjs";
import React from "react";
import { CustomCommands } from "@/shared/utils";
import {
  DEFAULT_GRID_ITEM_TEMPLATE,
  DEFAULT_LIST_ITEM_TEMPLATE,
  JOB_ITEM_TEMPLATE_GRID_PROP,
  JOB_ITEM_TEMPLATE_LIST_PROP,
  JOB_ITEM_VARIABLES,
} from "@/plugins/talentechFeed/jobItemTemplates";

const MonacoEditor = React.lazy(() => import("@monaco-editor/react"));

type TemplateView = "grid" | "list";

const JobItemTemplateModal: React.FC = () => {
  const editor = useEditor();
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState<TemplateView>("grid");
  const [gridTemplate, setGridTemplate] = useState(DEFAULT_GRID_ITEM_TEMPLATE);
  const [listTemplate, setListTemplate] = useState(DEFAULT_LIST_ITEM_TEMPLATE);
  const [targetComponent, setTargetComponent] = useState<Component | null>(null);

  const availableVariables = useMemo(() => {
    const variables = [
      ...JOB_ITEM_VARIABLES.filter(
        ({ key }) => !["AD_TEXT", "CONTACT_PERSONS"].includes(key)
      ),
      { key: "BUTTON_LABEL", label: "Button label" },
      { key: "JOB_URL", label: "Job URL" },
    ];

    return variables
      .slice()
      .sort((a, b) => a.key.localeCompare(b.key));
  }, []);

  const resolveComponent = (
    editorInstance: Editor,
    componentId?: string
  ) => {
    if (componentId) {
      const wrapper = editorInstance.getWrapper();
      const match = wrapper?.find(`#${componentId}`)?.[0];
      if (match) {
        return match;
      }
    }
    return editorInstance.getSelected() || null;
  };

  const copyToClipboard = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
    } catch (error) {
      const fallback = document.createElement("textarea");
      fallback.value = value;
      fallback.style.position = "fixed";
      fallback.style.opacity = "0";
      document.body.appendChild(fallback);
      fallback.select();
      document.execCommand("copy");
      document.body.removeChild(fallback);
    }
  };

  useEffect(() => {
    editor.Commands.add(
      CustomCommands.MODAL_JOBITEM_TEMPLATE_OPEN,
      (_editor: Editor, _sender: any, options?: { componentId?: string }) => {
        const component = resolveComponent(editor, options?.componentId);
        setTargetComponent(component);
        if (component) {
          setGridTemplate(
            component.get(JOB_ITEM_TEMPLATE_GRID_PROP) ||
              DEFAULT_GRID_ITEM_TEMPLATE
          );
          setListTemplate(
            component.get(JOB_ITEM_TEMPLATE_LIST_PROP) ||
              DEFAULT_LIST_ITEM_TEMPLATE
          );
        } else {
          setGridTemplate(DEFAULT_GRID_ITEM_TEMPLATE);
          setListTemplate(DEFAULT_LIST_ITEM_TEMPLATE);
        }
        setShowModal(true);
      }
    );
    editor.Commands.add(CustomCommands.MODAL_JOBITEM_TEMPLATE_CLOSE, () =>
      setShowModal(false)
    );
    editor.Commands.add(
      CustomCommands.MODAL_JOBITEM_TEMPLATE_SAVE,
      () => {
        if (!targetComponent) {
          setShowModal(false);
          return;
        }
        targetComponent.set(JOB_ITEM_TEMPLATE_GRID_PROP, gridTemplate);
        targetComponent.set(JOB_ITEM_TEMPLATE_LIST_PROP, listTemplate);
        setShowModal(false);
      }
    );
  }, [editor, gridTemplate, listTemplate, targetComponent]);

  const onSave = () =>
    editor.runCommand(CustomCommands.MODAL_JOBITEM_TEMPLATE_SAVE);

  return (
    <Modal
      title="Job item templates"
      size={BootstrapSizes.XLARGE}  
      showModal={showModal}
      onClose={() =>
        editor.runCommand(CustomCommands.MODAL_JOBITEM_TEMPLATE_CLOSE)
      }
      buttons={[
        <Button
          key="close"
          outline
          label="Close"
          color={ButtonColors.PRIMARY}
          onClick={() =>
            editor.runCommand(CustomCommands.MODAL_JOBITEM_TEMPLATE_CLOSE)
          }
        />,
        <Button
          key="save"
          label="Save"
          color={ButtonColors.PRIMARY}
          onClick={onSave}
        />,
      ]}
    >
      <div className="d-flex gap-3 flex-column">
        <div className="btn-group" role="tablist" aria-label="Template view">
          <button
            type="button"
            className={`btn btn-outline-primary ${
              activeTab === "grid" ? "active" : ""
            }`}
            aria-pressed={activeTab === "grid"}
            onClick={() => setActiveTab("grid")}
          >
            GRID
          </button>
          <button
            type="button"
            className={`btn btn-outline-primary ${
              activeTab === "list" ? "active" : ""
            }`}
            aria-pressed={activeTab === "list"}
            onClick={() => setActiveTab("list")}
          >
            LIST
          </button>
        </div>

        <div className="d-flex gap-3">
          <div className="flex-grow-1">
            <Suspense fallback={<div>Loading editor...</div>}>
              {activeTab === "grid" && (
                <MonacoEditor
                  height="380px"
                  language="html"
                  theme="vs-dark"
                  value={gridTemplate}
                  onChange={(value) => setGridTemplate(value || "")}
                  keepCurrentModel={false}
                />
              )}
              {activeTab === "list" && (
                <MonacoEditor
                  height="380px"
                  language="html"
                  theme="vs-dark"
                  value={listTemplate}
                  onChange={(value) => setListTemplate(value || "")}
                  keepCurrentModel={false}
                />
              )}
            </Suspense>
          </div>
          <aside className="border rounded p-2 w-25 flex-shrink-0">
            <div className="fw-semibold mb-2">JOBITEM variables</div>
            <div className="d-flex flex-column gap-1 small">
              {availableVariables.map((item) => {
                const token = `{JOBITEM.${item.key}}`;
                return (
                  <button
                    key={item.key}
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    title="Copy variable"
                    onClick={() => copyToClipboard(token)}
                  >
                    <code>{`{${item.key}}`}</code>
                    {/* <span className="text-muted"> — {item.label}</span> */}
                  </button>
                );
              })}
            </div>
          </aside>
        </div>
      </div>
    </Modal>
  );
};

export default JobItemTemplateModal;
